import { defineStore } from 'pinia'
import { parseSheet } from '@/composables/useSheet'
import type { CatalogItem, ProductModel, CategoryModel } from '@/types/catalog'

interface BuildResult {
  models: ProductModel[]
  categories: CategoryModel[]
}

const CANONICAL_ORDER = ['One Size', 'XS', 'SM', 'MD', 'LG', 'XL', 'XXL', 'XXXL'] as const
type CanonicalSize = typeof CANONICAL_ORDER[number]

function normalizeSizeCode(raw: string): CanonicalSize | string {
  const s = String(raw || '').trim().toUpperCase()
  if (!s) return ''
  if (['ONE SIZE', 'OS', 'O/S'].includes(s)) return 'One Size'
  if (s === 'XS') return 'XS'
  if (s === 'S' || s === 'SM') return 'SM'
  if (s === 'M' || s === 'MD') return 'MD'
  if (s === 'L' || s === 'LG') return 'LG'
  if (s === 'XL') return 'XL'
  if (s === 'XXL' || s === '2XL') return 'XXL'
  if (s === 'XXXL' || s === '3XL') return 'XXXL'
  return raw
}

function computeSizeRange(variants: CatalogItem[]): string | undefined {
  const raw = variants
    .map(v => String(v.SizeCode || v.Size || '').trim())
    .filter(Boolean)
  if (!raw.length) return undefined
  const normalized = raw.map(normalizeSizeCode)
  const unique: string[] = []
  for (const c of normalized) if (!unique.includes(c)) unique.push(c)

  if (unique.includes('One Size')) {
    const others = unique.filter(u => u !== 'One Size' && CANONICAL_ORDER.includes(u as CanonicalSize))
    if (!others.length) return 'One Size'
  }

  const inOrder = unique.filter(u => CANONICAL_ORDER.includes(u as CanonicalSize)) as CanonicalSize[]
  if (!inOrder.length) return unique.join(', ')
  const sorted = [...new Set(inOrder)].sort(
    (a, b) => CANONICAL_ORDER.indexOf(a) - CANONICAL_ORDER.indexOf(b)
  )
  const first = sorted[0]
  const last = sorted[sorted.length - 1]
  return first === last ? first : `${first}-${last}`
}

const PRICE_FIELDS = [
  'WSCAD','MSRPCAD',
  'WSUSD','MSRPUSD',
  'WSEUR','MSRPEUR','DISTEUR',
  'WSGBP','MSRPGBP',
  'WSCHF','MSRPCHF'
]

const RATING_FIELDS = [
  'TemperatureRatingOutof5',
  'InsulationRatingOutof5',
  'WindProtectionRatingOutof5',
  'WaterproofnessRatingOutof5',
  'BreathabilityRatingOutof5',
  'AirPermiabilityRatingOutof5',
  'IntensityOutof5'
] as const

function firstPrice(variants: CatalogItem[], key: string) {
  for (const v of variants) {
    const val = v[key]
    if (val != null && val !== '' && !Number.isNaN(Number(val))) return val
  }
  return null
}

function firstRating(variants: CatalogItem[], key: string): number | string | null {
  for (const v of variants) {
    const val = v[key]
    if (val != null && val !== '' && !Number.isNaN(Number(val))) return val
  }
  return null
}

function firstComparisonVariant(variants: CatalogItem[]): CatalogItem | undefined {
  return variants.find(v => v.IncludeinComparisonGuide) || variants[0]
}

function filterCategoriesByTopCategory(categories: CategoryModel[], topCategory?: string): CategoryModel[] {
  const filterVal = topCategory && topCategory !== 'All' ? topCategory : ''
  if (!filterVal) return categories
  return categories
    .map(cat => {
      const ProductModels = cat.ProductModels.filter(m =>
        m.variants.some(v => {
          const val = v.Category
          return val && String(val) === filterVal
        })
      )
      return { ...cat, ProductModels }
    })
    .filter(cat => cat.ProductModels.length > 0)
}

function buildCatalog(items: CatalogItem[]): BuildResult {
  const styleMap = new Map<string | number, CatalogItem[]>()
  for (const item of items) {
    if (!styleMap.has(item.Style)) styleMap.set(item.Style, [])
    styleMap.get(item.Style)!.push(item)
  }

  // Bygg kategori-map och order
  const categoryMap = new Map<string, CategoryModel>()
  const categoryOrder: string[] = []

  // Samla alla unika ProductFamilySlug
  for (const item of items) {
    const slug = item.ProductFamilySlug || 'other'
    if (!categoryMap.has(slug)) {
      categoryMap.set(slug, {
        Slug: slug,
        Name: item.Subcategory || item.ProductFamily || item.WBCategory || 'Other',
        IncludeinComparisonGuide: false,
        ProductModels: []
      })
      categoryOrder.push(slug)
    }
  }

  const models: ProductModel[] = []
  for (const [style, variants] of styleMap) {
    const master = variants[0]!
    const masterVariant = firstComparisonVariant(variants)
    const includeInComparisonGuide = !!masterVariant?.IncludeinComparisonGuide

    const ratings: Record<string, number | string | null> = {}
    for (const key of RATING_FIELDS) {
      ratings[key] = firstRating(variants, key)
    }

    const model: ProductModel = {
      name: master.Description,
      Style: style,
      Description: master.Description,
      Gender: master.Gender,
      ProductDescription: master.ProductDescription,
      ProductDetails: master.ProductDetails,
      Materials: master.Materials,
      Features: master.Features,
      Sustainability: master.Sustainability,
      ProductFit: master.ProductFit,
      ProductFamily: master.ProductFamily,
      ProductFamilySlug: master.ProductFamilySlug,
      ProductDetail: masterVariant?.ProductDetail || master.ProductDetail,
      ProductPrimaryUse: master.ProductPrimaryUse,
      ProductMaterialType: master.ProductMaterialType,
      SizeRange: computeSizeRange(variants),
      CountryOfOrigin: master.CountryOfOrigin || master.CountryofOrigin,
      CountryofOrigin: master.CountryOfOrigin || master.CountryofOrigin,
      Weight: master.Weightg || master.Weight,
      Weightg: master.Weightg || master.Weight,

      DetailDescription1: master.DetailDescription1,
      DetailDescription2: master.DetailDescription2,
      DetailDescription3: master.DetailDescription3,

      RoadLineOverview: master.RoadLineOverview,
      GravelLineOverview: master.GravelLineOverview,
      TrailLineOverview: master.TrailLineOverview,
      ExploreLineOverview: master.ExploreLineOverview,

      NOTAvailableforPREOrder: master.NOTAvailableforPREOrder,

      CompletetheKit: master.CompletetheKit,

      New: variants.some(v => v.New),
      Revised: variants.some(v => v.Revised),
      NewColour: variants.some(v => v.NewColour),
      GoreePE: variants.some(v => v.GoreePE),
      Helios: variants.some(v => v.Helios),
      WTV: variants.some(v => v.WTV),
      Airmap: variants.some(v => v.Airmap),
      StashSystem: variants.some(v => v.StashSystem),

      ...PRICE_FIELDS.reduce((acc, key) => {
        acc[key] = firstPrice(variants, key)
        return acc
      }, {} as Record<string, any>),
      ...ratings,

      IncludeinComparisonGuide: includeInComparisonGuide,
      masterVariant,

      variants
    }

    models.push(model)
  }

  for (const m of models) {
    const slug = m.ProductFamilySlug || 'other'
    const cat = categoryMap.get(slug)
    if (cat) cat.ProductModels.push(m)
  }
  for (const cat of categoryMap.values()) {
    cat.IncludeinComparisonGuide = cat.ProductModels.some(m => m.IncludeinComparisonGuide)
  }

  const categories = categoryOrder.map(slug => categoryMap.get(slug)!)
  return { models, categories }
}

export const useCatalogStore = defineStore('catalog', {
  state: () => ({
    items: [] as CatalogItem[],
    models: [] as ProductModel[],
    categories: [] as CategoryModel[],
    isLoading: false,
    error: null as string | null
  }),
  getters: {
    categoryBySlug: (s) => (slug: string) => s.categories.find(c => c.Slug === slug),
    topCategories(state): string[] {
      const set = new Set<string>()
      for (const item of state.items) {
        const cat = item.Category
        if (cat) set.add(String(cat))
      }
      return Array.from(set)
    },
    categoriesMap(state): Record<string, CategoryModel> {
      const m: Record<string, CategoryModel> = {}
      for (const c of state.categories) m[c.Slug] = c
      return m
    },
    modelByStyle: (s) => (style: string | number) => s.models.find(m => m.Style === style),
    comparisonCategories(state): CategoryModel[] {
      return state.categories.filter(cat => cat.IncludeinComparisonGuide === true)
    },
    categoriesByTopCategory: (state) => (topCategory?: string) =>
      filterCategoriesByTopCategory(state.categories, topCategory)
  },
  actions: {
    async fetchCatalog(sheetId: string, sheetPage: string) {
      if (this.isLoading) return
      this.isLoading = true
      this.error = null
      try {
        const parsed = await parseSheet(sheetId, sheetPage)
        this.items = parsed
        const { models, categories } = buildCatalog(this.items)
        this.models = models
        this.categories = categories
      } catch (e: any) {
        this.error = e?.message || 'Kunde inte hämta katalog.'
      } finally {
        this.isLoading = false
      }
    }
  }
})
